import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Send } from "lucide-react";
import api from "@/lib/api";
import { PageHero, Loading } from "@/components/common";
import FlashCard, { FLASH_TYPE_LABELS } from "@/components/FlashCard";
import { useAuth } from "@/context/AuthContext";

export default function Relampago() {
  const [today, setToday] = useState(undefined);
  const [closed, setClosed] = useState(null);
  const { user, login } = useAuth();
  const [prop, setProp] = useState({ question: "", context: "", source: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/flash/today").then((r) => setToday(r.data)).catch(() => setToday(null));
    api.get("/flash", { params: { scope: "closed" } }).then((r) => setClosed(r.data)).catch(() => setClosed([]));
  }, []);

  const sendProposal = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Inicia sesión para proponer una pregunta."); login(); return; }
    if (prop.question.trim().length < 8) { toast.error("Escribe una pregunta más completa."); return; }
    setBusy(true);
    try {
      const r = await api.post("/flash/propose", prop);
      toast.success(r.data.message);
      setProp({ question: "", context: "", source: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar."); } finally { setBusy(false); }
  };

  return (
    <div data-testid="relampago-page">
      <PageHero label="2Voto Relámpago" title="Una pregunta. 24 horas. La ciudadanía responde."
        subtitle="Cada día, una consulta breve sobre un tema de actualidad. Responde en menos de 20 segundos y vuelve mañana para la próxima pregunta." />

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-5">
          <Zap className="w-5 h-5 text-rose-500" />
          <h2 className="font-heading font-black text-2xl text-slate-900">Encuesta Relámpago de Hoy</h2>
        </div>
        {today === undefined ? <Loading /> : today === null ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500" data-testid="flash-none">
            No hay una encuesta relámpago activa en este momento. Vuelve pronto.
          </div>
        ) : <FlashCard poll={today} />}

        {/* Archive */}
        <div className="mt-14">
          <h2 className="font-heading font-black text-2xl text-slate-900 mb-6">Resultados anteriores</h2>
          {closed === null ? <Loading /> : closed.length === 0 ? (
            <p className="text-slate-400">Aún no hay consultas cerradas.</p>
          ) : (
            <div className="space-y-3">
              {closed.map((p) => (
                <Link key={p.id} to={`/relampago/${p.id}`} data-testid={`flash-archive-${p.id}`}
                  className="flex items-center justify-between bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-all">
                  <div>
                    <span className="text-xs font-semibold text-cyan-600 uppercase tracking-wide">{FLASH_TYPE_LABELS[p.poll_type]}</span>
                    <p className="font-medium text-slate-900 mt-0.5">{p.question}</p>
                  </div>
                  <span className="text-sm text-slate-400 shrink-0 ml-4">{p.total_votes?.toLocaleString("es-PR")} votos →</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Propose */}
        <div className="mt-14 bg-slate-900 rounded-2xl p-7" data-testid="flash-propose">
          <h2 className="font-heading font-black text-2xl text-white mb-2">Envía la próxima pregunta</h2>
          <p className="text-slate-300 text-sm mb-5">Propón una pregunta que la ciudadanía debería responder durante 24 horas. Revisamos neutralidad, claridad, relevancia y capacidad de medición. La identidad del proponente permanece confidencial.</p>
          <form onSubmit={sendProposal} className="space-y-3">
            <input value={prop.question} onChange={(e) => setProp({ ...prop, question: e.target.value })} data-testid="propose-question"
              placeholder="Tu pregunta (neutral y clara)" className="w-full rounded-lg px-4 py-3 outline-none text-sm" />
            <input value={prop.context} onChange={(e) => setProp({ ...prop, context: e.target.value })} data-testid="propose-context"
              placeholder="Contexto breve (opcional)" className="w-full rounded-lg px-4 py-3 outline-none text-sm" />
            <input value={prop.source} onChange={(e) => setProp({ ...prop, source: e.target.value })} data-testid="propose-source"
              placeholder="Fuente del asunto (opcional)" className="w-full rounded-lg px-4 py-3 outline-none text-sm" />
            <button type="submit" disabled={busy} data-testid="propose-submit"
              className="inline-flex items-center gap-2 bg-cyan-500 text-white font-semibold px-6 py-3 rounded-full hover:bg-cyan-600 transition-colors disabled:opacity-60">
              <Send className="w-4 h-4" /> {busy ? "Enviando…" : "Enviar pregunta"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
